// What a screen shows while its data loads, refreshes and fails (useLoad in ./ui keeps the state).
import { ApiError, errorMessage } from './api';

/** A refresh that failed while the screen still shows what it loaded before. */
export interface Stale {
  readonly error: string;
  /** no network, or the server restarting behind its proxy: not an answer from the API */
  readonly offline: boolean;
  /** when the data on screen was loaded (ms) */
  readonly since: number;
}

export interface LoadState<T> {
  readonly data: T | undefined;
  /** the latest load failed and there is nothing current to show */
  readonly error: string | null;
  readonly stale: Stale | null;
  /** the deps generation the data was loaded for (-1: none yet) */
  readonly epoch: number;
  readonly loadedAt: number;
}

export const initialLoad: LoadState<never> = { data: undefined, error: null, stale: null, epoch: -1, loadedAt: 0 };

export type LoadOutcome<T> = { readonly epoch: number; readonly at: number } & ({ readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: unknown });

export const isOffline = (error: unknown) => error instanceof ApiError && [0, 502, 503, 504].includes(error.status);

/**
 * A load's result. A failed refresh keeps the data loaded for the same deps and marks it stale: a
 * blip on the floor's Wi-Fi must not wipe a half-done screen. A failure is the screen's error only
 * when nothing current is shown (the first load, or other deps: another warehouse, another record).
 */
export function settle<T>(state: LoadState<T>, outcome: LoadOutcome<T>): LoadState<T> {
  if (outcome.ok) return { data: outcome.value, error: null, stale: null, epoch: outcome.epoch, loadedAt: outcome.at };
  const message = errorMessage(outcome.error);
  if (state.epoch === outcome.epoch) {
    return { ...state, error: null, stale: { error: message, offline: isOffline(outcome.error), since: state.loadedAt } };
  }
  return { ...state, error: message, stale: null };
}

/** "Bağlantı zayıf — liste 3 dk önceki hali" */
export function staleText(stale: Pick<Stale, 'offline' | 'since'>, now: number): string {
  const minutes = Math.max(0, Math.floor((now - stale.since) / 60_000));
  const age = minutes < 1 ? 'az' : minutes < 60 ? `${minutes} dk` : `${Math.floor(minutes / 60)} sa`;
  return `${stale.offline ? 'Bağlantı zayıf' : 'Yenilenemedi'} — liste ${age} önceki hali`;
}
